/* eslint-disable @next/next/no-img-element */
import React, { FC, useContext, useState } from "react";
import { GetServerSideProps } from "next";
import { StructuredText } from "react-datocms";
import { format } from "date-fns";
import RockstarFooter from "components/RockstarFooter";
import RockstarHeader from "components/RockstarHeader";
import { endpoints } from "config";
import HeadTagsRenderer from "components/HeadTagsRenderer";
import { WpYoastHead } from "types/wp-yoast-head";
import { LogContext } from "context/log";
import { DatoEventFragment, getSdk } from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import RockstarRelatedProducts from "components/RockstarRelatedProducts";
import TopCtaBanner from "components/TopCtaBanner";
import Link from "next/link";

interface EventsPageProps {
  events: DatoEventFragment[];
}

export const getServerSideProps: GetServerSideProps<EventsPageProps> = async (
  context
) => {
  try {
    const { allEvents } = await getSdk(
      createDatoClient(context.preview)
    ).AllEvents();

    return {
      props: {
        events: allEvents,
      },
    };
  } catch (e) {
    return {
      props: {
        events: [],
      },
    };
  }
};

const EventsPage: FC<EventsPageProps> = ({ events }) => {
  const { useLogPageView } = useContext(LogContext);
  useLogPageView(`events`);

  const [activeMenuOption, setActiveMenuOption] = useState<string>("upcoming");

  const pastEvents = events?.filter(
    (event) =>
      event?.eventDate &&
      new Date(event?.eventDate).getTime() < new Date().getTime()
  );

  const upcomingEvents = events?.filter(
    (event) =>
      !event?.eventDate ||
      (event?.eventDate &&
        new Date(event?.eventDate).getTime() > new Date().getTime())
  );

  upcomingEvents.sort(function compare(a, b) {
    return new Date(a.eventDate).valueOf() - new Date(b.eventDate).valueOf();
  });

  pastEvents.sort(function compare(a, b) {
    return new Date(b.eventDate).valueOf() - new Date(a.eventDate).valueOf();
  });

  const seoTags: WpYoastHead = {
    title: "Event Posts Archive - Rockstar Energy",
    canonical: `${endpoints.web}/events/`,
    og_locale: "en_US",
    og_type: "website",
    og_title: "Event Posts Archive - Rockstar Energy",
    og_url: `${endpoints.web}/events/`,
    og_site_name: "Rockstar Energy",
    twitter_card: "summary_large_image",
  };

  return (
    <>
      <HeadTagsRenderer yoastJson={seoTags} />

      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader showMusicPlayer={true} />

          <div id="content" className="site-content">
            <div id="primary" className="content-area">
              <main id="main" className="site-main" role="main">
                <div className="events-page">
                  <div className="container has-text-align-center entry-header alignwide">
                    <h2 className="entry-title">Fuel What’s Next</h2>
                    <p>Go on a journey to what’s next.</p>
                  </div>

                  <div className="events-list-block">
                    <div className="container">
                      <div className="event-filter-list">
                        <ul>
                          <li
                            className={
                              activeMenuOption === "past"
                                ? "active-event-tab"
                                : ""
                            }
                            onMouseEnter={() => {
                              setActiveMenuOption("past");
                            }}
                          >
                            <a title="" data-value="past-event">
                              PAST
                            </a>
                          </li>
                          <li
                            className={
                              activeMenuOption === "upcoming"
                                ? "active-event-tab"
                                : ""
                            }
                            onMouseEnter={() => {
                              setActiveMenuOption("upcoming");
                            }}
                          >
                            <a title="" data-value="upcoming-event">
                              WHAT’S NEXT
                            </a>
                          </li>
                        </ul>
                        {activeMenuOption === "past" && (
                          <div className="past-events-block event-content active-events past-event">
                            {pastEvents?.map((event) => (
                              <div key={event?.id} className="event-post-item">
                                <div className="event-post-img">
                                  <div className="event-img-wrapper">
                                    <div
                                      className="event-img"
                                      style={{
                                        backgroundImage: `url('${event?.image?.url}')`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                                <div className="event-post-details">
                                  <div className="event-post-info">
                                    <div className="event-header-info">
                                      <h3>
                                        {event?.title || ""}
                                        <div>
                                          <div className="date-container">
                                            <p className="event-date">
                                              {format(
                                                new Date(
                                                  event?.eventDate + "T00:00:00"
                                                ),
                                                "LLL d"
                                              )}
                                            </p>
                                            {event?.endDate ? (
                                              <p className="event-date">
                                                <span>-</span>
                                                {format(
                                                  new Date(
                                                    event?.endDate + "T00:00:00"
                                                  ),
                                                  "LLL d"
                                                )}
                                              </p>
                                            ) : (
                                              <></>
                                            )}
                                          </div>
                                        </div>
                                      </h3>
                                    </div>
                                    <div className="event-exc">
                                      <StructuredText
                                        data={event?.content?.value}
                                      />
                                      {event?.linkedArticle && (
                                        <Link
                                          href={`/${
                                            event?.linkedArticle?.slug || ""
                                          }`}
                                        >
                                          <a
                                            href={`/${
                                              event?.linkedArticle.slug || ""
                                            }`}
                                            title={
                                              event?.linkedArticle?.title || ""
                                            }
                                            className="btn feature-btn"
                                          >
                                            View Event Details
                                          </a>
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {activeMenuOption === "upcoming" && (
                          <div className="upcoming-events-block event-content active-events upcoming-event">
                            {upcomingEvents?.map((event) => (
                              <div key={event?.id} className="event-post-item">
                                <div className="event-post-img">
                                  <div className="event-img-wrapper">
                                    <div
                                      className="event-img"
                                      style={{
                                        backgroundImage: `url('${event?.image?.url}')`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                                <div className="event-post-details">
                                  <div className="event-post-info">
                                    <div className="event-header-info">
                                      <h3>
                                        {event?.title || ""}
                                        <div className="date-container">
                                          <p className="event-date">
                                            {format(
                                              new Date(
                                                event?.eventDate + "T00:00:00"
                                              ),
                                              "LLL d"
                                            )}
                                          </p>
                                          {event?.endDate ? (
                                            <p className="event-date">
                                              <span>-</span>
                                              {format(
                                                new Date(
                                                  event?.endDate + "T00:00:00"
                                                ),
                                                "LLL d"
                                              )}
                                            </p>
                                          ) : (
                                            <></>
                                          )}
                                        </div>
                                      </h3>
                                    </div>
                                    <div className="event-exc">
                                      <StructuredText
                                        data={event?.content?.value}
                                      />
                                      {event?.linkedArticle && (
                                        <Link
                                          href={`/${
                                            event?.linkedArticle?.slug || ""
                                          }`}
                                        >
                                          <a
                                            href={`/${
                                              event?.linkedArticle.slug || ""
                                            }`}
                                            title={
                                              event?.linkedArticle?.title || ""
                                            }
                                            className="btn feature-btn"
                                          >
                                            View Event Details
                                          </a>
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <RockstarRelatedProducts />
                </div>
              </main>
            </div>
          </div>

          <RockstarFooter />
        </div>
      </div>
    </>
  );
};

export default EventsPage;
